import { useContext } from 'react';

import { di } from 'react-magnetic-di';
import { fetchQuery, graphql, ReactRelayContext } from 'react-relay';

import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { RovoAgentAri } from '@atlassian/ari/rovo/agent';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { normalizeAccountIdFromIdentityAri } from '@atlassian/conversation-assistant-utils/default-agent';

import type { useAgentRoutingIdByIdentityAccountIdQuery } from './__generated__/useAgentRoutingIdByIdentityAccountIdQuery.graphql';

const ROVO_AGENTS_FE_TEAM_NAME = 'rovo-agents-fe';
const RELAY_READY_QUERY_KEY_PART = 'relay-ready';
const RELAY_MISSING_QUERY_KEY_PART = 'relay-missing';

type UseAgentRoutingIdByIdentityAccountIdProps = {
	identityAccountId: string;
	enabled?: boolean;
} & AssistanceServiceInjectedParams;

type UseAgentRoutingIdByIdentityAccountIdResult = {
	data: string | null;
	isFetching: boolean;
	error: ReturnType<typeof useQuery>['error'];
};

/**
 * Resolves the Studio id needed for message routing without waiting for
 * scenario metadata used by agent-details surfaces.
 */
export const useAgentRoutingIdByIdentityAccountId: (
	props: Omit<UseAgentRoutingIdByIdentityAccountIdProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => UseAgentRoutingIdByIdentityAccountIdResult = createHookWithAssistanceService(
	({
		identityAccountId,
		assistanceServiceInjectedParams: { experienceTracker, siteId },
		enabled = true,
	}: UseAgentRoutingIdByIdentityAccountIdProps): UseAgentRoutingIdByIdentityAccountIdResult => {
		di(fetchQuery);
		const environment = useContext(ReactRelayContext)?.environment ?? null;

		const { data, error, isFetching } = useQuery({
			queryKey: [
				'fetchAgentRoutingIdByIdentityAccountId',
				siteId,
				environment ? RELAY_READY_QUERY_KEY_PART : RELAY_MISSING_QUERY_KEY_PART,
				identityAccountId,
			],
			queryFn: async () => {
				experienceTracker.start({
					id: ExperienceName.FETCH_AGENT_BY_IDENTITY_ACCOUNT_ID,
					name: ExperienceName.FETCH_AGENT_BY_IDENTITY_ACCOUNT_ID,
				});

				if (!environment) {
					throw new Error('A Relay environment is required to resolve agent routing');
				}

				const cloudId = siteId.trim();
				if (!cloudId) {
					throw new Error('A cloud id is required to resolve agent routing');
				}

				const accountId = normalizeAccountIdFromIdentityAri(identityAccountId);
				if (!accountId) {
					throw new Error('A valid Identity User account id is required to resolve agent routing');
				}

				const identityUserAri = IdentityUserAri.create({ userId: accountId }).toString();
				const response = await fetchQuery<useAgentRoutingIdByIdentityAccountIdQuery>(
					environment,
					graphql`
						query useAgentRoutingIdByIdentityAccountIdQuery($cloudId: String!, $id: ID!) {
							agentStudio_agentByIdentityAccountId(cloudId: $cloudId, id: $id)
								@optIn(to: "AgentStudio") {
								__typename
								... on AgentStudioAssistant {
									id
								}
								... on QueryError {
									message
								}
							}
						}
					`,
					{
						cloudId,
						id: identityUserAri,
					},
					{ fetchPolicy: 'store-or-network' },
				).toPromise();

				const agentResult = response?.agentStudio_agentByIdentityAccountId;
				if (agentResult?.__typename === 'QueryError') {
					throw new Error(agentResult.message ?? 'Agent routing lookup failed');
				}
				if (agentResult?.__typename !== 'AgentStudioAssistant') {
					throw new Error('Agent routing lookup did not return an AgentStudio assistant');
				}

				try {
					const routingId = RovoAgentAri.parse(agentResult.id).agentId.trim();
					if (!routingId) {
						throw new Error();
					}
					return routingId;
				} catch {
					throw new Error('Agent routing lookup did not return a valid Rovo agent ARI');
				}
			},
			onSuccess: () => {
				experienceTracker.succeed({
					name: ExperienceName.FETCH_AGENT_BY_IDENTITY_ACCOUNT_ID,
				});
			},
			onError: (queryError) => {
				experienceTracker.fail({
					name: ExperienceName.FETCH_AGENT_BY_IDENTITY_ACCOUNT_ID,
					error: queryError,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});
			},
			enabled,
			refetchOnWindowFocus: true,
		});

		return { data, error, isFetching };
	},
);
